import { Point } from './Point.js'
import { Shape } from './Shape.js'
import { movePointAtAngle, getMidpoint } from '@Utils/Coordinates.js'
import * as Utils from '@Utils/Math.js'

let imageCache = {}
function loadImage(url) {
	if (imageCache[url]) {
		return imageCache[url]
	}
	let image = new Image()
	image.src = url
	imageCache[url] = image
	return image
}
export class Line extends Shape {
	p1
	p2
	hitBoxPadding = 20
	hitBoxPolygon = []
	constructor(p1, p2, properties = {}) {
		super()
		this.setp1(p1, false)
		this.setp2(p2, false)
		this.updateHitBox()
		this.properties = properties
		let _this = this
		if (properties.imageUrl) {
			let image = loadImage(properties.imageUrl)
			_this.image = image
			image.onload = () => {
				_this.trigger('changed', { imageLoaded: true })
			}
		}
	}
	setp1(point, shouldUpdateHitBox = true) {
		if (!point) {
			console.error('point is null')
			return
		}
		if (point instanceof Point) {
			this.p1 = point
			this.p1.parent = this
		} else {
			this.p1.x = point.x
			this.p1.y = point.y
		}
		if (shouldUpdateHitBox) {
			this.updateHitBox()
		}
		this.children = [this.p1, this.p2]
	}
	setp2(point, shouldUpdateHitBox = true) {
		if (!point) {
			console.error('point is null')
			return
		}
		if (point instanceof Point) {
			this.p2 = point
			this.p2.parent = this
		} else {
			this.p2.x = point.x
			this.p2.y = point.y
		}
		if (shouldUpdateHitBox) {
			this.updateHitBox()
		}
		this.children = [this.p1, this.p2]
	}
	updateHitBox() {
		let p1 = this.p1
		let p2 = this.p2
		let a = Math.atan2(p2.y - p1.y, p2.x - p1.x)
		this.angle = a
		let distance = this.hitBoxPadding
		this.hitBoxPolygon = [
			[p1.x + distance * Math.cos(a + Math.PI / 2), p1.y + distance * Math.sin(a + Math.PI / 2)],
			[p1.x - distance * Math.cos(a + Math.PI / 2), p1.y - distance * Math.sin(a + Math.PI / 2)],
			[p2.x - distance * Math.cos(a + Math.PI / 2), p2.y - distance * Math.sin(a + Math.PI / 2)],
			[p2.x + distance * Math.cos(a + Math.PI / 2), p2.y + distance * Math.sin(a + Math.PI / 2)],
		]
	}
	move(dx, dy) {
		this.p1.x += dx
		this.p1.y += dy
		this.p2.x += dx
		this.p2.y += dy
		this.updateHitBox()
	}
	handleForPoint(point) {
		if (this.p1.isInside(point)) {
			return this.p1
		}
		if (this.p2.isInside(point)) {
			return this.p2
		}
		return this.isInside(point) ? this : null
	}
	isInside(point) {
		if (this.p1.isInside(point)) {
			return true
		}
		if (this.p2.isInside(point)) {
			return true
		}
		let vs = this.hitBoxPolygon
		let x = point.x, y = point.y

		let inside = false
		for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
			let xi = vs[i][0], yi = vs[i][1]
			let xj = vs[j][0], yj = vs[j][1]

			let intersect = ((yi > y) != (yj > y))
				&& (x < (xj - xi) * (y - yi) / (yj - yi) + xi)
			if (intersect) inside = !inside
		}

		return inside
	}
	getLength() {
		return Math.sqrt(Math.pow(this.p2.x - this.p1.x, 2) + Math.pow(this.p2.y - this.p1.y, 2))
	}
	render(context, options = {
		pixelsPerMeter: 1
	}) {
		const line = this
		const pixelsPerMeter = options.pixelsPerMeter ?? 1
		let color = this.properties.color || 'black'
		let textColor = this.properties.textColor || 'black'
		const objectColor = color
		if (this.selected) {
			color = 'rgba(255,190,0,1)'
		} else if (this.highlighted) {
			color = 'rgba(255,190,0,0.5)'
		}
		const lineWidth = 4
		if (this.image) {
			let width = (this.properties.imageWidth || 5)
			const image = this.image
			const ratio = this.image.height / this.image.width
			const angle = Math.atan2(this.p2.y - this.p1.y, this.p2.x - this.p1.x)
			const distance = Math.sqrt(Math.pow(this.p2.x - this.p1.x, 2) + Math.pow(this.p2.y - this.p1.y, 2))
			width = distance * ratio * width / 50
			context.save()
			context.translate(this.p1.x, this.p1.y)
			context.rotate(angle)
			context.drawImage(image, 0, width / -2, distance, width)
			context.restore()
		} else if (this.properties.inclination) {
			const width = (this.properties.width ?? 50)
			const distance = Math.sqrt(Math.pow(this.p2.x - this.p1.x, 2) + Math.pow(this.p2.y - this.p1.y, 2))
			const angle = Math.atan2(this.p2.y - this.p1.y, this.p2.x - this.p1.x)
			context.save()
			context.translate(this.p2.x, this.p2.y)
			context.rotate(angle + Utils.degreesToRadians(180))
			const path = new Path2D()
			const ellipse = width / 2
			path.moveTo(0, 0)
			path.lineTo(distance - ellipse, width / 2)
			path.bezierCurveTo(distance, width / 2, distance, 0, distance, 0)
			path.bezierCurveTo(distance, 0, distance, width / -2, distance - ellipse, width / -2)
			path.lineTo(distance - ellipse, width / -2)
			path.lineTo(0, 0)
			context.fillStyle = objectColor
			context.fill(path)
			context.restore()
		} else {
			context.save()
			if (this.properties.dashed) {
				context.setLineDash([8, 8])
			}
			context.lineWidth = lineWidth
			context.strokeStyle = color
			context.beginPath()
			context.moveTo(line.p1.x, line.p1.y)
			context.lineTo(line.p2.x, line.p2.y)
			context.stroke()
			context.beginPath()
			context.restore()
		}
		if (this.properties.measurements) {
			const angle = Math.atan2(this.p2.y - this.p1.y, this.p2.x - this.p1.x)
			const distance = Math.sqrt(Math.pow(this.p2.x - this.p1.x, 2) + Math.pow(this.p2.y - this.p1.y, 2))
			const midpoint = getMidpoint([this.p1.x, this.p1.y], [this.p2.x, this.p2.y])
			const fontSize = Math.round(30)
			let text = Math.round(distance) + 'px'
			const center = movePointAtAngle(midpoint, angle, 20)
			if (pixelsPerMeter) {
				text = Math.round(distance / pixelsPerMeter) + ' m'
			}
			context.save()
			context.fillStyle = textColor
			context.textAlign = 'center'
			context.baseline = 'middle'
			context.font = `500 ${fontSize}pt/1 "Tietoevry sans 1",sans-serif`
			context.translate(center[0], center[1])
			context.rotate(angle)
			context.fillText(text, 0, 0)
			context.restore()

			// if (!line.selected && !line.highlighted) {
			// 	let lineLength = 20
			// 	context.save()
			// 	context.lineWidth = lineWidth
			// 	context.strokeStyle = color
			// 	context.beginPath()
			// 	context.moveTo(this.p1.x - lineLength * Math.cos(angle + Math.PI / 2), this.p1.y - lineLength * Math.sin(angle + Math.PI / 2))
			// 	context.lineTo(this.p1.x + lineLength * Math.cos(angle + Math.PI / 2), this.p1.y + lineLength * Math.sin(angle + Math.PI / 2))
			// 	context.stroke()
			// 	context.beginPath()
			// 	context.moveTo(this.p2.x - lineLength * Math.cos(angle + Math.PI / 2), this.p2.y - lineLength * Math.sin(angle + Math.PI / 2))
			// 	context.lineTo(this.p2.x + lineLength * Math.cos(angle + Math.PI / 2), this.p2.y + lineLength * Math.sin(angle + Math.PI / 2))
			// 	context.stroke()
			// 	context.restore()
			// }
		}
		if (line.selected || line.highlighted) {
			line.p1.render(context, options)
			line.p2.render(context, options)
		}
	}
	toJSON() {
		return {
			type: 'Line',
			p1: this.p1,
			p2: this.p2,
			properties: this.properties
		}
	}
	tidyRotation() {
		/// rotate the line around the center to the nearest 90 degree angle
		const angle = Math.atan2(this.p2.y - this.p1.y, this.p2.x - this.p1.x)
		let angleDegrees = Utils.radiansToDegrees(angle)
		if(angleDegrees > 0) {
			if(angleDegrees < 45) {
				angleDegrees = 0
			} else if (angleDegrees < 135) {
				angleDegrees = 90
			} else if (angleDegrees < 225) {	
				angleDegrees = 180
			} else if (angleDegrees < 315) {
				angleDegrees = 270
			} else {
				angleDegrees = 0
			}
		} else {
			if(angleDegrees > -45) {
				angleDegrees = 0
			} else if (angleDegrees > -135) {
				angleDegrees = -90
			} else if (angleDegrees > -225) {	
				angleDegrees = -180
			} else if (angleDegrees > -315) {
				angleDegrees = -270
			} else {
				angleDegrees = 0
			}
		}
		angleDegrees += 90
		const angleRadians = Utils.degreesToRadians(angleDegrees)
		const distance = Math.sqrt(Math.pow(this.p2.x - this.p1.x, 2) + Math.pow(this.p2.y - this.p1.y, 2))
		const midpoint = getMidpoint([this.p1.x, this.p1.y], [this.p2.x, this.p2.y])
		const p1 = movePointAtAngle(midpoint, angleRadians, distance / -2)
		const p2 = movePointAtAngle(midpoint, angleRadians, distance / 2)
		this.setp1(new Point(p1[0], p1[1]), false)
		this.setp2(new Point(p2[0], p2[1]), false)
	}
	getType() {
		return 'Line'
	}
}